import type { ApolloClient } from '@apollo/client'
import { TypedDocumentString } from '../utils/apollo'

type ApolloFragmentIdentity = {
  __typename: string
} & Record<string, unknown>

type UseApolloWatchFragmentParams<Fragment> = {
  client: () => ApolloClient
  from: () => ApolloFragmentIdentity | None
  fragment: MyDocumentNode
  fragmentName: string
  optimistic?: boolean
  on: (doc: Fragment | undefined) => void
  onError?: (error: unknown) => void
}

export function useApolloWatchFragment<Fragment>(inp: UseApolloWatchFragmentParams<Fragment>): void {
  const client = inp.client()

  let subscription:
    | {
        unsubscribe(): void
      }
    | undefined

  function stopSubscription(): void {
    subscription?.unsubscribe()
    subscription = undefined
  }

  const stopWatch = watch(
    () => {
      const from = inp.from()
      return from ? client.cache.identify(from as any) : undefined
    },
    (cacheId) => {
      stopSubscription()

      if (!cacheId) {
        inp.on(undefined)
        return
      }

      /*
       * SSR only needs the normal read performed by useMultigetDeboucePost.
       * Do not leave long-lived fragment observers on the server.
       */
      if (import.meta.server) return

      const observable = client.watchFragment<Fragment>({
        from: cacheId,
        fragment: TypedDocumentString.getQueryDocument(inp.fragment),
        fragmentName: inp.fragmentName,
        optimistic: inp.optimistic ?? true,
      })

      function apply(result: ReturnType<typeof observable.getCurrentResult>): void {
        if (!result.complete) {
          inp.on(undefined)
          return
        }

        inp.on(result.data as Fragment)
      }

      /*
       * Publish the already-cached value immediately.
       */
      apply(observable.getCurrentResult())

      subscription = observable.subscribe({
        next: apply,
        error(error) {
          if (inp.onError) {
            inp.onError(error)
            return
          }

          console.error('[apollo watchFragment]', error)
        },
      })
    },
    { immediate: true }
  )

  onScopeDispose(() => {
    stopWatch()
    stopSubscription()
  })
}

// oxlint-disable-next-line explicit-module-boundary-types
export function createUseApolloWatchFragment(client: () => ApolloClient) {
  return function <Fragment>(inp: Omit<UseApolloWatchFragmentParams<Fragment>, 'client'>): void {
    return useApolloWatchFragment<Fragment>({
      client,
      ...inp,
    })
  }
}
